/**
 * Screen 5 of 5 — SIGN-OFF AND FINALIZE.
 *
 * VALUATION-B3 gave it a real engagement-level attestation record; Finalize
 * writes to that.
 *
 * What actually happens when Finalize is pressed:
 *
 *   1. `POST .../finalize` re-runs the engine on the reviewed inputs, refuses
 *      the whole thing if any blocker stands (incomplete questionnaire,
 *      unresolved out-of-range override, tuned config with no rationale), and
 *      otherwise freezes a `ValuationSnapshot` — inputs, outputs, AND the
 *      methodology config that produced them.
 *   2. The signature comes off the verified token. `signed_by` is not on the
 *      wire, because a sign-off a caller can address to somebody else is not a
 *      sign-off.
 *   3. Only then does `can_produce_document` go true.
 *
 * Three rules this screen renders rather than decides, all enforced server-side:
 *
 *   * **Preview flags, finalize fences.** `/recompute` reports an out-of-range
 *     override at the value the CPA stated and widens the band. `/finalize`
 *     will not freeze it. The blocker list here comes from `/preflight`, which
 *     runs the same gate without writing anything.
 *   * **Re-finalize supersedes, it never overwrites.** A new version is
 *     written, the prior is marked superseded and retained, and only one is
 *     live — "no two live valuations that disagree."
 *   * **A record must still reproduce.** Every read replays the frozen inputs
 *     through the frozen config; the verdict is rendered on screen.
 *
 * The inputs are the SAVED draft, not a browser cache: the server signs what
 * is saved; finalize sends only the sign-off reason. Nothing saved -> Intake.
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { useAuth } from '@/contexts/AuthContext'
import {
  fetchValuationDocuments,
  getValuationEngagement,
  updateValuationEngagement,
  type ValuationDocumentRow,
} from '@/api/valuation'
import {
  fetchActiveConfig,
  fetchSnapshot,
  fetchSnapshotHistory,
  finalizeValuation,
  preflightValuation,
  produceValuationDocument,
} from '@/api/valuationB3'
import { formatApiError } from '@/utils/httpErrors'
import type { ValuationEngagement } from '@/types/valuation'
import type {
  ConfigResponse,
  PreflightResponse,
  SnapshotHistoryRow,
  SnapshotResponse,
} from '@/types/valuationB3'
import { FinalizeCard, ProduceDocumentCard, SignOffGates, START_NEW_VERSION, isDelivered } from './_SignOffGates'
import { PendingReview, FrozenReview } from './_SignOffReview'
import { SnapshotHistory, VerificationPanel } from './_SnapshotHistory'
import { SnapshotDownloadButton } from './_SnapshotDownload'
import { sameReason } from './_signOffRationale'
import { useValuationDraft } from './_valuationDraft'
import { useSignoffReason } from './_useDraftExtras'
import { bodyFromDraft } from './_valuationTuningHelpers'
import { DeliverableCard, StatusCard } from './_SignOffStatus'
import { SignOffActionBar } from './_SignOffActionBar'
import {
  StatusBadge,
  ValuationStepper,
  ValueKindBadge,
  statusLabel,
  useCachedRun,
  useValuationEntitlement,
  type ValuationStatus,
} from './_shared'

/**
 * Plain labels for the "Changed since vN: ..." line. Whether anything changed
 * is decided once, by the server (`preflight.reissue`, VAL-REISSUE-SERVER-1) —
 * this screen only names the fields it is told moved. `answers` is the key
 * `inputs_to_dict()` uses for `RecomputeBody.driver_answers`.
 */
const REISSUE_FIELD_LABELS: Record<string, string> = {
  benchmark_key: 'benchmark',
  normalized_earnings: 'normalized earnings',
  basis: 'earnings basis',
  income_value: 'income approach value',
  total_assets: 'total assets',
  total_liabilities: 'total liabilities',
  fair_value_adjustments: 'fair value adjustments',
  override_multiple: 'override multiple',
  override_rationale: 'override rationale',
  override_disposition: 'override disposition',
  naics_code: 'NAICS code',
  sub_vertical: 'sub-vertical',
  market_only_acknowledged: 'market-only acknowledgement',
  risk_free_rate: 'risk-free rate',
  company_specific_risk: 'company-specific risk',
  long_term_growth_rate: 'long-term growth rate',
  equity_risk_premium: 'equity risk premium',
  size_premium: 'size premium',
  growth_rate: 'growth rate',
  projection_years: 'projection years',
  capitalized_earnings_weight: 'capitalized earnings weight',
  base_cash_flow: 'base cash flow',
  replacement_compensation: 'replacement compensation',
  comp_multiples: 'comparable company multiples',
  config_fingerprint: 'methodology config',
}

/** "3 driver answers" for `answers` (counting individual differing entries,
 *  not the one field name) — every other field maps to its plain label. */
function humanizeChangedField(
  key: string,
  frozenInputs: Record<string, unknown>,
  candidateAnswers: Record<string, string>,
): string {
  if (key === 'answers') {
    const frozen = (frozenInputs.answers as Record<string, string> | undefined) ?? {}
    const keys = new Set([...Object.keys(frozen), ...Object.keys(candidateAnswers)])
    let count = 0
    keys.forEach(k => { if (frozen[k] !== candidateAnswers[k]) count += 1 })
    return `${count} driver answer${count === 1 ? '' : 's'}`
  }
  return REISSUE_FIELD_LABELS[key] ?? key
}

export default function ValuationSignOff() {
  const { valuationId } = useParams<{ valuationId: string }>()
  const navigate = useNavigate()
  const { user } = useAuth()
  const { canConclude } = useValuationEntitlement()
  const run = useCachedRun(valuationId)
  const {
    draft, draftTuning, draftExtras, updatedAt, savedAtLabel, resumedAtLabel, loading: draftLoading,
  } = useValuationDraft(valuationId)
  // The review panel's view of what is saved; the server never receives it.
  const tuningBody = useMemo(
    () => (draft ? bodyFromDraft(draft, draftTuning) : null),
    [draft, draftTuning],
  )
  const hasSavedDraft = !!tuningBody

  const [engagement, setEngagement] = useState<ValuationEngagement | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)

  const [config, setConfig] = useState<ConfigResponse | null>(null)
  const [snapshot, setSnapshot] = useState<SnapshotResponse | null>(null)
  const [history, setHistory] = useState<SnapshotHistoryRow[]>([])
  const [preflight, setPreflight] = useState<PreflightResponse | null>(null)
  const [preflightError, setPreflightError] = useState<string | null>(null)
  const [rationale, setRationale] = useSignoffReason(valuationId, draftExtras.signoff_reason ?? undefined, hasSavedDraft)
  const [finalizing, setFinalizing] = useState(false)
  const [producing, setProducing] = useState(false)
  const [documents, setDocuments] = useState<ValuationDocumentRow[]>([])
  const [pdfStatus, setPdfStatus] = useState<string | null>(null)
  // null = the user has not touched it: open by default when inputs changed since the signed version (state B).
  const [newVersionOpen, setNewVersionOpen] = useState<boolean | null>(null)

  /** Snapshot + history together — after a finalize both have moved. */
  const loadRecord = useCallback(async (id: string) => {
    const [snap, hist] = await Promise.all([fetchSnapshot(id), fetchSnapshotHistory(id)])
    setSnapshot(snap)
    setHistory(hist.snapshots)
  }, [])

  const loadDocuments = useCallback(async (id: string) => {
    const res = await fetchValuationDocuments(id)
    setDocuments(res.documents)
  }, [])

  useEffect(() => {
    if (!valuationId) return
    let live = true
    getValuationEngagement(valuationId)
      .then(en => { if (live) setEngagement(en) })
      .catch((err: unknown) => { if (live) setLoadError(formatApiError(err)) })
    loadRecord(valuationId).catch((err: unknown) => {
      // A missing record is not an error — nothing has been signed yet. Only a
      // genuine failure to read it is, and the screen still works without it.
      if (live) toast.error(formatApiError(err))
    })
    loadDocuments(valuationId).catch((err: unknown) => {
      // An engagement with no documents is not an error — nothing produced yet.
      if (live) toast.error(formatApiError(err))
    })
    fetchActiveConfig()
      .then(c => { if (live) setConfig(c) })
      .catch(() => { /* the review panel falls back to the preflight numbers */ })
    return () => { live = false }
  }, [valuationId, loadRecord, loadDocuments])

  /**
   * Re-run the gate when the tuning inputs change — NOT on the rationale. The
   * rationale changes no number the engine returns (it is volatile in the
   * fingerprint, `core/valuation/config_codec.py`), so typing it must not
   * trigger a re-run: `canFinalize` below enforces the blank-rationale rule
   * client-side, and `/finalize` enforces it server-side, strictly, at the
   * point of freezing.
   */
  useEffect(() => {
    if (!valuationId || !hasSavedDraft) return
    let live = true
    const timer = setTimeout(() => {
      preflightValuation(valuationId)
        .then(p => { if (live) { setPreflight(p); setPreflightError(null) } })
        .catch((err: unknown) => { if (live) setPreflightError(formatApiError(err)) })
    }, 400)
    return () => { live = false; clearTimeout(timer) }
  }, [valuationId, hasSavedDraft, updatedAt])

  async function setStatus(next: ValuationStatus): Promise<boolean> {
    if (!valuationId) return false
    setSaving(true)
    try {
      const updated = await updateValuationEngagement(valuationId, { status: next })
      setEngagement(updated)
      toast.success(`Status set to “${statusLabel(next)}”.`)
      return true
    } catch (err: unknown) {
      toast.error(formatApiError(err))
      return false
    } finally {
      setSaving(false)
    }
  }

  async function doFinalize() {
    if (!valuationId || !hasSavedDraft) return
    setFinalizing(true)
    try {
      const result = await finalizeValuation(valuationId, rationale.trim())
      // The server cleared the draft's signoff_reason with this finalize; the box starts empty for the next version.
      setRationale('')
      toast.success(
        result.superseded_version === null
          ? `Finalized v${result.snapshot.version}.`
          : `Finalized v${result.snapshot.version} — v${result.superseded_version} superseded.`,
      )
      await loadRecord(valuationId)
      // VAL-FINALIZE-STATE-2: the preflight in state was computed against the OLD version; drop it
      // and read it again so the bar does not offer Re-finalize as v{N+2} on a version just signed.
      setPreflight(null)
      preflightValuation(valuationId)
        .then(p => { setPreflight(p); setPreflightError(null) })
        .catch((err: unknown) => setPreflightError(formatApiError(err)))
      // The band on the row was just restated by the server; re-read it rather
      // than patching the copy in state from the response.
      const fresh = await getValuationEngagement(valuationId)
      setEngagement(fresh)
      // VAL-SIGNOFF-GATE-1: only a credentialed finalize signs — the status
      // marker follows what actually happened, not what this screen used to
      // assume happened. An owner's finalize records the calculation; it does
      // not become "Signed off" just because a snapshot now exists (see the
      // module docstring and _SignOffStatus.tsx: status is a workflow marker,
      // the snapshot's signed_role is the attestation).
      const target: ValuationStatus = canConclude ? 'signed_off' : 'calculated'
      const settled = fresh.status !== target ? await setStatus(target) : true
      // VAL-WALK-SCREENS-1 W7: tell the shell once the status is really settled,
      // so its badge and side menu refresh without a navigation.
      if (settled) {
        window.dispatchEvent(new CustomEvent('valuation:engagement-changed', { detail: { id: valuationId } }))
      }
    } catch (err: unknown) {
      toast.error(formatApiError(err))
    } finally {
      setFinalizing(false)
    }
  }

  /**
   * Print the frozen record. The version on screen is sent explicitly rather
   * than letting the server pick "current" — if a colleague re-finalized while
   * this tab sat open, the mismatch comes back as the gate's refusal instead of
   * silently handing over a document with a different basis than the one
   * reviewed here.
   *
   * This no longer downloads anything itself — it archives the document(s)
   * and lists what got produced. Files are saved from the card's list.
   */
  async function handleProduce() {
    if (!valuationId) return
    setProducing(true)
    try {
      const response = await produceValuationDocument(
        valuationId,
        snapshot?.snapshot?.version,
      )
      setPdfStatus(response.pdf_status)
      const count = response.documents.length
      toast.success(`v${response.snapshot_version} produced — ${count} file${count === 1 ? '' : 's'} ready.`)
    } catch (err: unknown) {
      toast.error(formatApiError(err))
    } finally {
      // Success or 409, the server's list and on-file flag are the truth.
      await Promise.all([loadDocuments(valuationId), loadRecord(valuationId)]).catch(
        (e: unknown) => toast.error(formatApiError(e)),
      )
      setProducing(false)
      // The left rail reads the same on-file flag for its NEXT marker.
      window.dispatchEvent(new CustomEvent('valuation:engagement-changed', { detail: { id: valuationId } }))
    }
  }

  // Hoisted above the early returns: hooks below must run on every render.
  const frozen = snapshot?.snapshot ?? null
  const liveVersion = frozen && frozen.status === 'final' ? frozen.version : null

  // VAL-REISSUE-SERVER-1: the server's answer, from preflight — only
  // meaningful on a re-finalize (a live version exists).
  const reissueDelta = liveVersion !== null ? preflight?.reissue ?? null : null

  const changedFieldLabels = useMemo(() => {
    if (!reissueDelta || !frozen) return []
    const candidateAnswers = tuningBody?.driver_answers ?? {}
    return reissueDelta.changed_fields.map(key =>
      humanizeChangedField(key, frozen.inputs as Record<string, unknown>, candidateAnswers),
    )
  }, [reissueDelta, frozen, tuningBody])

  if (loadError) {
    return (
      <div className="mx-auto max-w-7xl p-6">
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800">{loadError}</div>
      </div>
    )
  }
  if (!engagement) {
    return (
      <div className="mx-auto max-w-7xl space-y-6 p-6">
        <div>
          <Skeleton className="h-8 w-64" />
          <Skeleton className="mt-2 h-4 w-40" />
        </div>
        <Skeleton className="h-10 w-full" />
        {Array.from({ length: 3 }).map((_, i) => (
          <Card key={i}>
            <CardHeader><Skeleton className="h-5 w-40" /></CardHeader>
            <CardContent className="space-y-3">
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-2/3" />
            </CardContent>
          </Card>
        ))}
      </div>
    )
  }

  const concluded = engagement.value_kind === 'concluded'
  const low = concluded ? engagement.concluded_value_low : engagement.calculated_value_low
  const high = concluded ? engagement.concluded_value_high : engagement.calculated_value_high
  // The engagement's PERSISTED band. Only Finalize writes this column pair
  // (VAL-INDUSTRY-WIRING-1 R2 step 1) — before the first Record/Finalize it
  // is always null. This is what the Deliverable card shows: what has
  // actually been recorded, not what could be.
  const hasBand = low !== null && high !== null

  // The in-session band (`preflight.computed`). The gate and Record key off
  // THIS, not `hasBand`: pre-finalize no persisted band exists (VAL-STATE-PERSIST-1 R1).
  const computed = preflight?.computed ?? null
  const hasComputedValue = computed !== null && computed.value_low !== null && computed.value_high !== null

  // VAL-SIGNOFF-GATE-1: a conclusion of value still requires canConclude — that
  // fence does not widen (S5). A calculation engagement does not — a
  // valuation_owner may finalize (record) one without it; `willSign` below is
  // what tells the card whether THIS finalize also signs.
  // VAL-REISSUE-GUARD-1 R3: on a re-finalize, nothing-changed is itself a
  // reason not to allow the button — the disabled state in FinalizeCard
  // handles the message, this handles the actual gate.
  // Preflight is debounced 400ms: until it answers `reissue` is undefined
  // (null = nothing signed), and the button must not read as "unchanged".
  const reissueFingerprintPending = liveVersion !== null && !!tuningBody && preflight?.reissue === undefined
  // VAL-SIGNOFF-REASON-DISTINCT-1: the sign-off reason is the signer's own
  // words; the box opens empty and a copy of the Methodology reason is refused.
  const methodologyReason = draftTuning?.rationale ?? ''
  const repeatsMethodology = sameReason(rationale, methodologyReason)
  const needsMethodologyReason = !!preflight?.tuned && preflight.tuning_rationale_recorded === false
  // FIX_VAL-SIGNER-SIGN-1: a valuation signer can sign a value the owner recorded with no
  // input change. "Nothing has changed" applies only once a signer has signed the live version.
  const awaitingSignature = liveVersion !== null && canConclude && frozen !== null
    && frozen.signed_role !== 'valuation_signer'
  const gateOk = !!tuningBody && !!preflight?.ok && hasComputedValue
    && !needsMethodologyReason
    && (concluded ? canConclude : true)
    && (reissueDelta?.changed !== false || awaitingSignature)
    && !reissueFingerprintPending
  const canFinalize = gateOk && rationale.trim().length > 0 && !repeatsMethodology
  const blockedReason = concluded && !canConclude
    ? 'A conclusion of value may only be signed by a credentialed valuation role.'
    : !tuningBody
      ? 'Save your inputs on Intake and Methodology, then come back to Finalize.'
      : reissueFingerprintPending
        ? 'Checking for changes…'
        : !hasComputedValue
          ? 'The engine has not returned a computed value for these inputs yet.'
          : rationale.trim().length === 0
            ? 'Write the reason for this version.'
            : repeatsMethodology
              ? 'The sign-off reason must be your own words — it matches the Methodology reason.'
              : needsMethodologyReason
                ? 'Write the Methodology reason on Methodology first.'
                : preflight && !preflight.ok
                  ? `Blocked: ${preflight.blockers.join(' · ')}`
                  : null

  const delivered = isDelivered(liveVersion, snapshot?.documents_on_file ?? false, snapshot?.can_produce_document ?? false)
  const inputsChanged = !reissueFingerprintPending && reissueDelta?.changed === true
  const cardOpen = newVersionOpen ?? (inputsChanged || awaitingSignature)
  // The bar's Re-finalize as v{N+1} button: open the new-version card, scroll to it, focus the reason.
  function openNewVersion() {
    setNewVersionOpen(true)
    setTimeout(() => {
      const box = document.getElementById('signoff-rationale')
      box?.scrollIntoView?.({ block: 'center' })
      box?.focus()
    }, 0)
  }
  const finalizeCard = (
    <FinalizeCard
      methodologyReason={methodologyReason}
      rationale={rationale}
      onRationaleChange={setRationale}
      canFinalize={canFinalize}
      blockedReason={blockedReason}
      finalizing={finalizing}
      onFinalize={doFinalize}
      liveVersion={liveVersion}
      willSign={canConclude}
      reissueChanged={reissueFingerprintPending || awaitingSignature ? null : (reissueDelta?.changed ?? null)}
      changedFieldLabels={changedFieldLabels}
      delivered={delivered}
      tuned={!!preflight?.tuned}
    />
  )

  return (
    <div className="mx-auto max-w-7xl space-y-6 p-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">{engagement.subject_company_name}</h1>
        <div className="mt-1 flex flex-wrap items-center gap-2 text-sm text-gray-500">
          <ValueKindBadge kind={engagement.value_kind} />
          <StatusBadge status={engagement.status} />
        </div>
      </div>

      <ValuationStepper current="signoff" engagementId={valuationId} />

      <DeliverableCard
        engagement={engagement}
        low={low}
        high={high}
        hasBand={hasBand}
        // A signed value shows the signed wording; the cached preview text is only for a preview.
        positioning={frozen ? snapshot?.statement : run?.positioning}
        previewLow={computed?.value_low}
        previewHigh={computed?.value_high}
      />

      <SignOffGates
        hasComputedValue={hasComputedValue}
        concluded={concluded}
        canConclude={canConclude}
        userName={user?.name}
        userRole={user?.role}
        hasTuningInputs={hasSavedDraft || draftLoading}
        preflight={preflight}
        preflightError={preflightError}
        onBackToTuning={() => navigate(`/valuation/${valuationId}/tuning`)}
        liveVersion={liveVersion}
        engagementId={valuationId}
      />

      {/* The four things a reader of the deliverable would challenge later:
          the weights, the reason they moved, the driver scores, the benchmark
          version. Before the signature they are a preview; after it they are
          read back out of the frozen record. */}
      {draftLoading && <p className="text-sm text-gray-500">Loading your saved inputs…</p>}
      {tuningBody && (savedAtLabel || resumedAtLabel) && (
        <p className="text-sm text-gray-500">{savedAtLabel ?? `Saved draft from ${resumedAtLabel}`}</p>
      )}
      {tuningBody && (
        <PendingReview
          body={tuningBody}
          basis={preflight}
          computed={preflight?.computed ?? null}
          config={config}
        />
      )}

      {!delivered && finalizeCard}

      {frozen && <FrozenReview collapsible snapshot={frozen} valueKind={engagement.value_kind} signedByName={snapshot?.signed_by_name} multipleSourceLabel={snapshot?.multiple_source_label} benchmarkSourceLabel={snapshot?.benchmark_source_label} />}
      {snapshot?.verification && <VerificationPanel verification={snapshot.verification} />}

      <ProduceDocumentCard
        engagementId={valuationId ?? ''}
        liveVersion={liveVersion}
        documentsOnFile={snapshot?.documents_on_file ?? false}
        concluded={concluded}
        canProduce={snapshot?.can_produce_document ?? false}
        note={
          user?.role === 'valuation_owner' && frozen && !engagement.full_report_paid
            ? 'Your estimate is recorded. This is an owner-prepared estimate — no valuation signer signs it.' +
              ' Your report will be available after payment.'
            : snapshot?.production_note ?? 'Finalize the valuation to unlock document production.'
        }
        onProduce={handleProduce}
        producing={producing}
        documents={documents}
        pdfStatus={pdfStatus}
      />

      {delivered && (
        <div>
          <button
            type="button"
            className="text-sm text-gray-600 underline underline-offset-2 hover:text-gray-900"
            aria-expanded={cardOpen}
            onClick={() => setNewVersionOpen(!cardOpen)}
          >
            {START_NEW_VERSION}
          </button>
          {cardOpen && <div className="mt-3">{finalizeCard}</div>}
        </div>
      )}

      {frozen && (
        (user?.role === 'valuation_owner' && engagement.snapshot_paid) ||
        ((user?.role === 'valuation_signer' || user?.role === 'super_admin') && engagement.snapshot_available)
      ) && (
        <SnapshotDownloadButton engagementId={valuationId ?? ''} />
      )}

      <SnapshotHistory rows={history} collapsible />

      <StatusCard
        collapsible
        engagement={engagement}
        canConclude={canConclude}
        saving={saving}
        onSetStatus={setStatus}
      />

      <div className="flex justify-between">
        <Button variant="outline" onClick={() => navigate(`/valuation/${valuationId}/output`)}>
          Back to value &amp; provenance
        </Button>
        <Button variant="outline" onClick={() => navigate('/valuation')}>
          All valuations
        </Button>
      </div>

      <SignOffActionBar
        engagementId={valuationId ?? ''}
        hasComputedValue={hasComputedValue}
        concluded={concluded}
        canConclude={canConclude}
        engineOk={preflight ? preflight.ok : null}
        liveVersion={liveVersion}
        willSign={canConclude}
        reissueChanged={reissueFingerprintPending || awaitingSignature ? null : (reissueDelta?.changed ?? null)}
        awaitingSignature={awaitingSignature}
        finalizing={finalizing}
        canFinalize={canFinalize}
        gateOk={gateOk}
        blockedReason={blockedReason}
        onFinalize={doFinalize}
        documentsOnFile={snapshot?.documents_on_file ?? false}
        canProduce={snapshot?.can_produce_document ?? false}
        producing={producing}
        onProduce={handleProduce}
        documents={documents}
        changedFieldLabels={changedFieldLabels}
        onOpenNewVersion={openNewVersion}
      />
    </div>
  )
}
